import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpRight, ArrowDownRight, Star, Target, Award } from 'lucide-react';
import { ProcessedOfficer, TIER_LABELS, formatHm, formatLongDate, getInitials } from '../lib/officerUtils';
interface OfficerDrawerProps {
  officer: ProcessedOfficer | null;
  teamAvg: {
    borrowerMin: number;
    availableMin: number;
    wrapUpMin: number;
    offQueueMin: number;
    focusPct: number;
    leadsConverted: number;
    submissions: number;
    productionScore: number;
  };
  onClose: () => void;
}
const RING_HEX: Record<ProcessedOfficer['tier'], string> = {
  pacesetter: '#0F5132',
  climbing: '#0E1116',
  steady: '#D9D3C4',
  coaching: '#C2410C'
};
export function OfficerDrawer({
  officer,
  teamAvg,
  onClose
}: OfficerDrawerProps) {
  useEffect(() => {
    if (!officer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [officer, onClose]);
  useEffect(() => {
    if (!officer) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [officer]);
  return <AnimatePresence data-id="element-104">
      {officer && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-ink/30 z-40" data-id="element-105" />

          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.32,
        ease: [0.22, 1, 0.36, 1]
      }} style={{
        backgroundColor: '#F7F4EE'
      }} className="fixed top-0 right-0 bottom-0 w-full sm:w-[560px] z-50 shadow-2xl overflow-y-auto border-l border-ink-10" role="dialog" aria-modal="true" data-id="element-106">
            <DrawerContent officer={officer} teamAvg={teamAvg} onClose={onClose} data-id="element-107" />
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function DrawerContent({
  officer,
  teamAvg,
  onClose
}: {
  officer: ProcessedOfficer;
  teamAvg: OfficerDrawerProps['teamAvg'];
  onClose: () => void;
}) {
  const ringColor = RING_HEX[officer.tier];
  const isPacesetter = officer.tier === 'pacesetter';
  const isCoaching = officer.tier === 'coaching';
  const breakdown: Array<{
    label: string;
    value: number;
    color: string;
    tone: 'positive' | 'caution' | 'negative' | 'muted' | 'total';
    note?: string;
  }> = [{
    label: 'On Call',
    value: officer.borrowerMin,
    color: '#0F5132',
    tone: 'positive',
    note: 'positive effort'
  }, {
    label: 'Ready',
    value: officer.availableMin,
    color: '#4A7C59',
    tone: 'positive',
    note: 'positive effort'
  }, {
    label: 'Wrap-up',
    value: officer.wrapUpMin,
    color: '#C68A4B',
    tone: 'caution',
    note: 'after-call work — minimize'
  }, {
    label: 'Not Ready',
    value: officer.offQueueMin,
    color: '#C2410C',
    tone: 'negative',
    note: 'off-queue'
  }, {
    label: 'Ringing',
    value: officer.ringingMin,
    color: 'rgba(14,17,22,0.45)',
    tone: 'muted',
    note: 'manual dial · rare'
  }, {
    label: 'Total Login',
    value: officer.loginMin,
    color: '#0E1116',
    tone: 'total'
  }];
  const comparisons = [{
    label: 'Submissions',
    officer: officer.submissions,
    team: teamAvg.submissions,
    format: (v: number) => v.toFixed(1)
  }, {
    label: 'Leads Converted',
    officer: officer.leadsConverted,
    team: teamAvg.leadsConverted,
    format: (v: number) => v.toFixed(1)
  }, {
    label: 'On Call',
    officer: officer.borrowerMin,
    team: teamAvg.borrowerMin,
    format: formatHm
  }, {
    label: 'Ready',
    officer: officer.availableMin,
    team: teamAvg.availableMin,
    format: formatHm
  }, {
    label: 'Wrap-up',
    officer: officer.wrapUpMin,
    team: teamAvg.wrapUpMin,
    format: formatHm,
    inverse: true
  }, {
    label: 'Not Ready',
    officer: officer.offQueueMin,
    team: teamAvg.offQueueMin,
    format: formatHm,
    inverse: true
  }];
  return <div className="relative" data-id="element-108">
      <button type="button" onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-ink-50 hover:text-ink hover:bg-ink-5 transition-colors z-10" aria-label="Close drawer" data-id="element-109">
        <X className="w-5 h-5" strokeWidth={1.5} data-id="element-110" />
      </button>

      <div className="px-10 pt-12 pb-10" data-id="element-111">
        {/* Eyebrow */}
        <div className="font-serif font-bold text-xs tracking-[0.28em] uppercase text-primary/70 mb-6" data-id="element-112">
          Loan Officer · {formatLongDate(officer.date)}
        </div>

        {/* Identity */}
        <div className="flex items-center gap-5 mb-8" data-id="element-113">
          <div className="relative flex-shrink-0" data-id="element-114">
            <div className="w-16 h-16 rounded-full bg-bone-2 flex items-center justify-center font-serif font-bold text-2xl text-primary" style={{
            boxShadow: `0 0 0 2px ${ringColor}`
          }} data-id="element-115">
              {getInitials(officer.name)}
            </div>
            {isPacesetter && <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-signal flex items-center justify-center" data-id="element-116">
                <Star className="w-3.5 h-3.5 text-bone" fill="currentColor" strokeWidth={0} data-id="element-117" />
              </div>}
          </div>
          <div data-id="element-118">
            <div className="font-serif font-bold text-3xl text-primary leading-tight" data-id="element-119">
              {officer.name}
            </div>
            <div className="text-xs uppercase tracking-[0.22em] text-primary/50 mt-1.5 font-bold" data-id="element-120">
              {TIER_LABELS[officer.tier]}
            </div>
          </div>
        </div>

        {/* Production Score headline */}
        <div className="border-t border-b border-ink-10 py-6 mb-8" data-id="element-121">
          <div className="flex items-baseline justify-between mb-3" data-id="element-122">
            <div data-id="element-123">
              <div className="text-xs uppercase tracking-[0.22em] text-ink-70 font-medium mb-1.5" data-id="element-124">
                Production Score
              </div>
              <div className="text-xs text-ink-50" data-id="element-125">
                40% effort · 60% output (subs weighted 2× leads)
              </div>
            </div>
            <div className={`font-serif text-6xl tabular-nums ${isCoaching ? 'text-ember' : 'text-ink'}`} data-id="element-126">
              {officer.productionScore.toFixed(0)}
            </div>
          </div>

          {/* Output stat cards */}
          <div className="grid grid-cols-2 gap-px bg-ink-10 border border-ink-10 mt-5" data-id="element-127">
            <OutputStat icon={Target} label="Leads Converted" value={officer.leadsConverted} max={10} data-id="element-128" />
            <OutputStat icon={Award} label="Submissions" value={officer.submissions} max={6} data-id="element-129" />
          </div>
        </div>

        {officer.isWarmPhone && <div className="mb-8 px-4 py-3 border-l-2 border-ember bg-ember-soft/40 text-sm text-ink-70" data-id="element-130">
            <span className="font-medium text-ember" data-id="element-131">
              Warm phone, no production.
            </span>{' '}
            High focus % but zero leads and zero submissions today. Worth a
            coaching conversation about pipeline movement.
          </div>}

        {/* State Breakdown */}
        <div className="mb-10" data-id="element-132">
          <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50 mb-5" data-id="element-133">
            Time Breakdown
          </div>
          <div className="space-y-3" data-id="element-134">
            {breakdown.map(b => {
            const pct = officer.loginMin > 0 ? b.value / officer.loginMin * 100 : 0;
            const isTotal = b.tone === 'total';
            return <div key={b.label} className={`flex items-center justify-between ${isTotal ? 'pt-3 border-t border-ink-10' : ''}`} data-id="element-135">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-136">
                    {!isTotal && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{
                  backgroundColor: b.color
                }} data-id="element-137" />}
                    <div className="min-w-0" data-id="element-138">
                      <span className={`text-sm ${isTotal ? 'text-ink font-medium' : 'text-ink-70'}`} data-id="element-139">
                        {b.label}
                      </span>
                      {b.note && <span className="text-[11px] text-ink-50 ml-2" data-id="element-140">
                          {b.note}
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-3 tabular-nums flex-shrink-0" data-id="element-141">
                    {!isTotal && <span className="text-xs text-ink-50" data-id="element-142">
                        {pct.toFixed(0)}%
                      </span>}
                    <span className={`font-serif ${isTotal ? 'text-lg text-ink' : 'text-base text-ink'}`} data-id="element-143">
                      {formatHm(b.value)}
                    </span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* vs. Floor Average */}
        <div data-id="element-144">
          <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50 mb-5" data-id="element-145">
            vs. Floor Average
          </div>
          <div className="space-y-5" data-id="element-146">
            {comparisons.map(c => <ComparisonRow key={c.label} {...c} data-id="element-147" />)}
          </div>
        </div>

        {officer.hasPartialData && <div className="mt-8 px-4 py-3 border-l-2 border-ember bg-ember-soft/40 text-sm text-ink-70" data-id="element-148">
            <span className="font-medium text-ember" data-id="element-149">Partial reporting.</span>{' '}
            Only Login and Not Ready were captured for this officer. On Call,
            Ready, and Wrap-up data is missing for the day.
          </div>}
      </div>
    </div>;
}
function OutputStat({
  icon: Icon,
  label,
  value,
  max
}: {
  icon: any;
  label: string;
  value: number;
  max: number;
}) {
  const pct = Math.min(100, value / max * 100);
  const dots = Math.min(max, Math.max(value, 0));
  return <div className="p-5" style={{
    backgroundColor: '#F7F4EE'
  }} data-id="element-150">
      <div className="flex items-center gap-2 mb-3" data-id="element-151">
        <Icon className="w-3.5 h-3.5 text-ink-70" strokeWidth={1.5} data-id="element-152" />
        <span className="text-xs uppercase tracking-[0.18em] text-ink-70 font-medium" data-id="element-153">
          {label}
        </span>
      </div>
      <div className="font-serif font-bold text-4xl text-primary tabular-nums leading-none mb-3" data-id="element-154">
        {value}
      </div>
      <div className="flex gap-1" data-id="element-155">
        {Array.from({
        length: max
      }).map((_, i) => <motion.span key={i} initial={{
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.25,
        delay: 0.15 + i * 0.04
      }} className={`flex-1 h-1.5 rounded-full ${i < dots ? 'bg-signal' : 'bg-bone-2'}`} data-id="element-156" />)}
      </div>
    </div>;
}
function ComparisonRow({
  label,
  officer,
  team,
  format,
  inverse = false
}: {
  label: string;
  officer: number;
  team: number;
  format: (n: number) => string;
  inverse?: boolean;
}) {
  const max = Math.max(officer, team) || 1;
  const officerPct = officer / max * 100;
  const teamPct = team / max * 100;
  const diff = officer - team;
  const positive = inverse ? diff < 0 : diff > 0;
  const Icon = diff >= 0 ? ArrowUpRight : ArrowDownRight;
  return <div data-id="element-157">
      <div className="flex items-baseline justify-between mb-2" data-id="element-158">
        <span className="text-sm text-ink-70" data-id="element-159">{label}</span>
        <div className="flex items-center gap-3" data-id="element-160">
          <span className="font-serif font-bold text-base text-primary tabular-nums" data-id="element-161">
            {format(officer)}
          </span>
          <span className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${positive ? 'text-signal' : 'text-ember'}`} data-id="element-162">
            <Icon className="w-3 h-3" strokeWidth={2.5} data-id="element-163" />
            {format(Math.abs(diff))}
          </span>
        </div>
      </div>
      <div className="space-y-1.5" data-id="element-164">
        <div className="flex items-center gap-2" data-id="element-165">
          <span className="text-[10px] uppercase tracking-[0.18em] text-ink-50 w-16" data-id="element-166">
            Officer
          </span>
          <div className="flex-1 h-1.5 bg-bone-2 rounded-full overflow-hidden" data-id="element-167">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${officerPct}%`
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="h-full bg-ink rounded-full" data-id="element-168" />
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-169">
          <span className="text-[10px] uppercase tracking-[0.18em] text-ink-50 w-16" data-id="element-170">
            Floor avg
          </span>
          <div className="flex-1 h-1.5 bg-bone-2 rounded-full overflow-hidden" data-id="element-171">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${teamPct}%`
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: 'easeOut'
          }} className="h-full bg-ink-30 rounded-full" data-id="element-172" />
          </div>
        </div>
      </div>
    </div>;
}